import { keepPreviousData, skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { useState } from 'react';

import { translate } from '../../common/helpers/translate.helper';
import { useDebounce } from '../../common/hooks/useDebounce';

import { ComboBoxBase } from './ComboBoxBase';
import type { ComboBoxBaseProps } from './ComboBoxBase';
import { ComboBoxMode } from './types/ComboBoxMode';
import type { ComboBoxOption } from './types/ComboBoxOption';
import {
    ASYNC_OPTIONS_STALE_TIME,
    COMBO_BOX_SEARCH_DEBOUNCE_TIME,
    DISABLED_ASYNC_QUERY_KEY,
    EMPTY_OPTIONS_SEARCH,
} from './utils/ComboBoxConstants';
import { getSelectedOptions } from './utils/getSelectedOptions';

type DistributiveOmit<T, Key extends PropertyKey> = T extends unknown ? Omit<T, Key> : never;

type AsyncControlledComboBoxBaseProp =
    | 'async'
    | 'open'
    | 'search'
    | 'options'
    | 'selectedOptions'
    | 'onOpenChange'
    | 'onSearchChange'
    | 'isInitialLoading'
    | 'isSearchLoading'
    | 'isLoadingMoreOptions'
    | 'isSelectedOptionsLoading'
    | 'optionsErrorMessage'
    | 'selectedOptionsErrorMessage'
    | 'onTriggerPointerEnter'
    | 'onOptionsScrollEnd';

type AsyncComboBoxBaseSharedProps<Option extends ComboBoxOption> = DistributiveOmit<
    ComboBoxBaseProps<Option>,
    AsyncControlledComboBoxBaseProp
> & {
    optionsQueryKey: (search: string) => QueryKey;
    optionsQueryFn: (search: string) => Promise<Option[]>;
    prefetchOnHover?: boolean;
};

type AsyncSingleComboBoxBaseProps<Option extends ComboBoxOption> = AsyncComboBoxBaseSharedProps<Option> & {
    mode: ComboBoxMode.Single;
    selectedOptionQueryKey: (value: Option['value']) => QueryKey;
    selectedOptionQueryFn: (value: Option['value']) => Promise<Option | null>;
};

type AsyncMultiComboBoxBaseProps<Option extends ComboBoxOption> = AsyncComboBoxBaseSharedProps<Option> & {
    mode: ComboBoxMode.Multi;
    selectedOptionsQueryKey: (values: Array<Option['value']>) => QueryKey;
    selectedOptionsQueryFn: (values: Array<Option['value']>) => Promise<Option[]>;
};

type AsyncComboBoxBaseProps<Option extends ComboBoxOption> =
    | AsyncSingleComboBoxBaseProps<Option>
    | AsyncMultiComboBoxBaseProps<Option>;

const getSelectedValues = <Option extends ComboBoxOption>(props: AsyncComboBoxBaseProps<Option>) => {
    if (props.mode === ComboBoxMode.Single) {
        const selectedValue = props.value;

        return selectedValue === null || selectedValue === undefined ? [] : [selectedValue];
    }

    return props.value ?? [];
};

const getMissingSelectedValues = <Option extends ComboBoxOption>(
    selectedValues: Array<Option['value']>,
    options: Option[]
) => {
    const optionValueSet = new Set(options.map((option) => option.value));

    return selectedValues.filter((selectedValue) => !optionValueSet.has(selectedValue));
};

const getSelectedOptionsQueryKey = <Option extends ComboBoxOption>(
    props: AsyncComboBoxBaseProps<Option>,
    selectedValues: Array<Option['value']>
) => {
    if (selectedValues.length === 0) {
        return DISABLED_ASYNC_QUERY_KEY;
    }

    if (props.mode === ComboBoxMode.Single) {
        return props.selectedOptionQueryKey(selectedValues[0]);
    }

    return props.selectedOptionsQueryKey(selectedValues);
};

const getSelectedOptionsQueryFn = <Option extends ComboBoxOption>(
    props: AsyncComboBoxBaseProps<Option>,
    missingSelectedValues: Array<Option['value']>
) => {
    if (missingSelectedValues.length === 0) {
        return skipToken;
    }

    if (props.mode === ComboBoxMode.Single) {
        return async () => {
            const selectedOption = await props.selectedOptionQueryFn(missingSelectedValues[0]);

            return selectedOption ? [selectedOption] : [];
        };
    }

    return () => props.selectedOptionsQueryFn(missingSelectedValues);
};

export const AsyncComboBoxBase = <Option extends ComboBoxOption = ComboBoxOption>(
    componentProps: AsyncComboBoxBaseProps<Option>
) => {
    const { optionsQueryKey, optionsQueryFn, prefetchOnHover = true, ...baseProps } = componentProps;
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, COMBO_BOX_SEARCH_DEBOUNCE_TIME);
    const isSearchDebouncing = search !== debouncedSearch;
    const optionsQuery = useQuery({
        queryKey: optionsQueryKey(debouncedSearch),
        queryFn: () => optionsQueryFn(debouncedSearch),
        enabled: open,
        placeholderData: keepPreviousData,
        staleTime: ASYNC_OPTIONS_STALE_TIME,
    });
    const options = optionsQuery.data ?? [];
    const selectedValues = getSelectedValues(componentProps);
    const missingSelectedValues = getMissingSelectedValues(selectedValues, options);
    const selectedOptionsFromOptions = getSelectedOptions({
        value: selectedValues,
        options,
        selectedOptions: [],
    });
    const selectedOptionsQuery = useQuery({
        queryKey: getSelectedOptionsQueryKey(componentProps, selectedValues),
        queryFn: getSelectedOptionsQueryFn(componentProps, missingSelectedValues),
        initialData: missingSelectedValues.length === 0 ? selectedOptionsFromOptions : undefined,
        placeholderData: keepPreviousData,
        staleTime: ASYNC_OPTIONS_STALE_TIME,
    });
    const resolvedSelectedOptions = getSelectedOptions({
        value: selectedValues,
        options,
        selectedOptions: selectedOptionsQuery.data ?? [],
    });
    const isInitialLoading = open && optionsQuery.isPending && debouncedSearch.trim() === '' && !optionsQuery.data;
    const isSearchLoading =
        open && search.trim() !== '' && (isSearchDebouncing || optionsQuery.isFetching || optionsQuery.isPending);
    const isSelectedOptionsLoading =
        resolvedSelectedOptions.length < selectedValues.length &&
        !selectedOptionsQuery.isError &&
        (selectedOptionsQuery.isPending || selectedOptionsQuery.isPlaceholderData);
    const prefetchAsyncOptions = () => {
        if (!prefetchOnHover) {
            return;
        }

        queryClient.prefetchQuery({
            queryKey: optionsQueryKey(EMPTY_OPTIONS_SEARCH),
            queryFn: () => optionsQueryFn(EMPTY_OPTIONS_SEARCH),
            staleTime: ASYNC_OPTIONS_STALE_TIME,
        });
    };
    const selectedOptionsErrorMessage =
        missingSelectedValues.length > 0 && selectedOptionsQuery.isError
            ? translate('comboBox.couldNotLoadSelection')
            : undefined;

    if (baseProps.mode === ComboBoxMode.Single) {
        return (
            <ComboBoxBase<Option>
                async
                mode={ComboBoxMode.Single}
                value={baseProps.value}
                open={open}
                search={search}
                label={baseProps.label}
                labelInfo={baseProps.labelInfo}
                placeholder={baseProps.placeholder}
                searchPlaceholder={baseProps.searchPlaceholder}
                caption={baseProps.caption}
                CaptionIcon={baseProps.CaptionIcon}
                disabled={baseProps.disabled}
                positive={baseProps.positive}
                negative={baseProps.negative}
                markAsRequired={baseProps.markAsRequired}
                markAsOptional={baseProps.markAsOptional}
                description={baseProps.description}
                isClearable={baseProps.isClearable}
                isSearchable={baseProps.isSearchable}
                minWidth={baseProps.minWidth}
                dropdownWidth={baseProps.dropdownWidth}
                dropdownMaxHeight={baseProps.dropdownMaxHeight}
                noOptionsMessage={baseProps.noOptionsMessage}
                options={options}
                selectedOptions={selectedOptionsQuery.data ?? []}
                optionsErrorMessage={optionsQuery.isError ? translate('comboBox.couldNotLoadOptions') : undefined}
                selectedOptionsErrorMessage={selectedOptionsErrorMessage}
                onBlur={baseProps.onBlur}
                LeadingIcon={baseProps.LeadingIcon}
                onSelectionChange={baseProps.onSelectionChange}
                isInitialLoading={isInitialLoading}
                isSearchLoading={isSearchLoading}
                isSelectedOptionsLoading={isSelectedOptionsLoading}
                onOpenChange={setOpen}
                onSearchChange={setSearch}
                customOptionsFilter={baseProps.customOptionsFilter}
                onTriggerPointerEnter={prefetchAsyncOptions}
                showSelectedOptionAtTop={baseProps.showSelectedOptionAtTop}
                clearSearchOnSelect={baseProps.clearSearchOnSelect}
                OptionComponent={baseProps.OptionComponent}
                OptionSeparatorComponent={baseProps.OptionSeparatorComponent}
                ValueComponent={baseProps.ValueComponent}
            />
        );
    }

    return (
        <ComboBoxBase<Option>
            async
            mode={ComboBoxMode.Multi}
            value={baseProps.value}
            open={open}
            search={search}
            label={baseProps.label}
            labelInfo={baseProps.labelInfo}
            placeholder={baseProps.placeholder}
            searchPlaceholder={baseProps.searchPlaceholder}
            caption={baseProps.caption}
            CaptionIcon={baseProps.CaptionIcon}
            disabled={baseProps.disabled}
            positive={baseProps.positive}
            negative={baseProps.negative}
            markAsRequired={baseProps.markAsRequired}
            markAsOptional={baseProps.markAsOptional}
            description={baseProps.description}
            isClearable={baseProps.isClearable}
            isSearchable={baseProps.isSearchable}
            minWidth={baseProps.minWidth}
            dropdownWidth={baseProps.dropdownWidth}
            dropdownMaxHeight={baseProps.dropdownMaxHeight}
            noOptionsMessage={baseProps.noOptionsMessage}
            options={options}
            selectedOptions={selectedOptionsQuery.data ?? []}
            optionsErrorMessage={optionsQuery.isError ? translate('comboBox.couldNotLoadOptions') : undefined}
            selectedOptionsErrorMessage={selectedOptionsErrorMessage}
            onBlur={baseProps.onBlur}
            LeadingIcon={baseProps.LeadingIcon}
            onSelectionChange={baseProps.onSelectionChange}
            isInitialLoading={isInitialLoading}
            isSearchLoading={isSearchLoading}
            isSelectedOptionsLoading={isSelectedOptionsLoading}
            onOpenChange={setOpen}
            onSearchChange={setSearch}
            customOptionsFilter={baseProps.customOptionsFilter}
            onTriggerPointerEnter={prefetchAsyncOptions}
            showSelectedOptionAtTop={baseProps.showSelectedOptionAtTop}
            clearSearchOnSelect={baseProps.clearSearchOnSelect}
            OptionComponent={baseProps.OptionComponent}
            OptionSeparatorComponent={baseProps.OptionSeparatorComponent}
            ValueComponent={baseProps.ValueComponent}
        />
    );
};
